import type { ContributionDetail, GeoJSONGeometry } from '../services/api';

export type DividedCarriagewayGuide = { side: 'a' | 'b' | 'divide'; geometry: GeoJSON.LineString };

export function isLineStringGeometry(
  geom: GeoJSON.Geometry | GeoJSONGeometry | null | undefined,
): geom is GeoJSON.LineString {
  return (
    geom?.type === 'LineString'
    && Array.isArray(geom.coordinates)
    && geom.coordinates.length >= 2
  );
}

/** API extra_fields may store GeoJSON as a parsed object or JSON string. */
export function parseStoredGeometry(value: unknown): GeoJSON.Geometry | null {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as GeoJSON.Geometry;
    } catch {
      return null;
    }
  }
  if (typeof value === 'object' && value !== null && 'type' in value) {
    return value as GeoJSON.Geometry;
  }
  return null;
}

/** Both carriageways for a divided-road contribution (A = primary geometry, B = extra_fields). */
export function getDividedCarriagewayGuides(
  detail: Pick<ContributionDetail, 'geometry' | 'extra_fields'>,
): DividedCarriagewayGuide[] | null {
  const extra = detail.extra_fields;
  if (!extra?.divided_carriageway_pair) return null;
  const lineA = parseStoredGeometry(detail.geometry);
  if (!isLineStringGeometry(lineA)) return null;
  const bGeom = parseStoredGeometry(extra.carriageway_b_geometry);
  if (!isLineStringGeometry(bGeom)) return null;
  const guides: DividedCarriagewayGuide[] = [];
  const divideGeom = parseStoredGeometry(extra.divide_geometry);
  const showDivideGuide =
    isLineStringGeometry(divideGeom)
    && (
      extra.layout === 'divided'
      || extra.strip_a_capture_mode === 'derive_from_divide'
      || extra.strip_b_capture_mode === 'derive_from_divide'
    );
  if (showDivideGuide) {
    guides.push({ side: 'divide', geometry: divideGeom as GeoJSON.LineString });
  }
  guides.push(
    { side: 'a', geometry: lineA as GeoJSON.LineString },
    { side: 'b', geometry: bGeom as GeoJSON.LineString },
  );
  return guides;
}

/** All map geometries for a contribution (primary + divided carriageway B when present). */
export function collectContributionGeometries(
  detail: Pick<ContributionDetail, 'geometry' | 'extra_fields'>,
): GeoJSON.Geometry[] {
  const geometries: GeoJSON.Geometry[] = [];
  const primary = parseStoredGeometry(detail.geometry);
  if (primary) geometries.push(primary);
  const guides = getDividedCarriagewayGuides(detail);
  if (guides) {
    const b = guides.find((g) => g.side === 'b')?.geometry;
    if (b) geometries.push(b);
  }
  return geometries;
}

export function contributionMapBoundsGeometry(
  detail: Pick<ContributionDetail, 'geometry' | 'extra_fields'>,
): GeoJSON.Geometry | null {
  const geometries = collectContributionGeometries(detail);
  if (geometries.length === 0) return null;
  if (geometries.length === 1) return geometries[0];
  return { type: 'GeometryCollection', geometries };
}

/** Map center for contribution deep-link URL params. */
export function contributionDeepLinkCoords(
  detail: Pick<ContributionDetail, 'geometry' | 'extra_fields'>,
): { lat: number; lng: number; zoom: number } | null {
  const geom = parseStoredGeometry(detail.geometry);
  if (!geom) return null;

  if (geom.type === 'Point') {
    const [lng, lat] = geom.coordinates as [number, number];
    return { lat, lng, zoom: 17 };
  }

  if (geom.type === 'LineString' && geom.coordinates.length > 0) {
    const mid = geom.coordinates[Math.floor(geom.coordinates.length / 2)] as [number, number];
    return { lat: mid[1], lng: mid[0], zoom: 17 };
  }

  return null;
}

/** Feature types where GPS trace walks a closed perimeter → Polygon. */
const POLYGON_TRACE_FEATURES = new Set(['landuse', 'water', 'landcover', 'building', 'structure']);

export function traceProducesPolygon(featureTypeId?: string | null): boolean {
  if (!featureTypeId) return false;
  return POLYGON_TRACE_FEATURES.has(featureTypeId);
}

export function traceMinimumPoints(featureTypeId?: string | null): number {
  return traceProducesPolygon(featureTypeId) ? 3 : 2;
}

export function closeRing(coords: number[][]): number[][] {
  if (coords.length < 3) return coords;
  const first = coords[0];
  const last = coords[coords.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) return coords;
  return [...coords, first];
}

export function buildTraceGeometry(
  tracePoints: Array<{ lat: number; lng: number }>,
  featureTypeId?: string | null,
): GeoJSONGeometry | null {
  if (tracePoints.length === 0) return null;

  const coords = tracePoints.map((p) => [p.lng, p.lat] as [number, number]);
  const min = traceMinimumPoints(featureTypeId);

  if (tracePoints.length < min) {
    if (tracePoints.length === 1) {
      return { type: 'Point', coordinates: coords[0] };
    }
    return { type: 'LineString', coordinates: coords };
  }

  if (traceProducesPolygon(featureTypeId)) {
    const ring = closeRing(coords);
    return { type: 'Polygon', coordinates: [ring] };
  }

  return { type: 'LineString', coordinates: coords };
}

export function hasValidTraceGeometry(
  tracePoints: Array<{ lat: number; lng: number }>,
  featureTypeId?: string | null,
): boolean {
  return tracePoints.length >= traceMinimumPoints(featureTypeId);
}

/** Draw mode uses the same polygon vs line split as GPS trace. */
export function drawProducesPolygon(featureTypeId?: string | null): boolean {
  return traceProducesPolygon(featureTypeId);
}

/** Minimum vertices to enable Continue in draw mode (permissive — finish refining on submit). */
export function drawMinimumVertices(_featureTypeId?: string | null): number {
  return 1;
}

export function vertexCount(geometry: GeoJSON.Geometry): number {
  if (geometry.type === 'Point') return 1;
  if (geometry.type === 'LineString') return geometry.coordinates?.length ?? 0;
  if (geometry.type === 'Polygon') return geometry.coordinates?.[0]?.length ?? 0;
  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates.reduce((sum, poly) => sum + (poly[0]?.length ?? 0), 0);
  }
  return 0;
}

export function hasDrawableGeometry(
  geometry: GeoJSON.Geometry | null | undefined,
  featureTypeId?: string | null,
): boolean {
  if (!geometry) return false;
  return vertexCount(geometry) >= drawMinimumVertices(featureTypeId);
}
